import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Upload, X, File, AlertCircle } from "lucide-react";
import { API_ENDPOINTS } from "../config/api";
import apiRequest from "../utils/api";
import { CONTENT_TYPE_MAP } from "../utils/contentTypeMapping";

interface Product {
  id: number;
  title: string;
  author: string;
  price: number;
  category: string;
  imageUrl: string;
  description: string;
  type: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface FileUploadModalProps {
  product: Product;
  onClose: () => void;
  onSuccess: () => void;
}

export const FileUploadModal: React.FC<FileUploadModalProps> = ({
  product,
  onClose,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contentType, setContentType] = useState<string>("파일"); // 기본값: 파일
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    if (file && !title) {
      // 파일명에서 확장자를 제거한 이름을 제목으로 설정
      const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      setTitle(fileNameWithoutExt);
    }
    setError(null);
  };

  const sanitizeFileName = (fileName: string): string => {
    // 파일명을 안전하게 처리 (특수문자 제거, 공백을 언더스코어로 변경)
    return fileName
      .replace(/[^a-zA-Z0-9.-]/g, "_")
      .replace(/\s+/g, "_")
      .toLowerCase();
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !title.trim()) {
      setError("파일과 제목을 모두 입력해주세요.");
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // 파일명을 보안적으로 안전하게 처리
      const sanitizedFileName = sanitizeFileName(selectedFile.name);

      // presigned URL 요청 (강좌 컨텐츠용)
      const presignedResponse = await apiRequest(
        API_ENDPOINTS.PRESIGNED_URL.COURSE_CONTENT(product.id.toString()),
        {
          method: "POST",
          body: JSON.stringify({
            fileName: sanitizedFileName,
          }),
        }
      );

      if (!presignedResponse.ok) {
        throw new Error("Presigned URL 요청 실패");
      }

      const presignedData = await presignedResponse.json();

      if (!presignedData.success) {
        throw new Error("Presigned URL 생성 실패");
      }

      setUploadProgress(25);

      // presigned URL로 파일 업로드
      const uploadResponse = await fetch(presignedData.data.url, {
        method: "PUT",
        body: selectedFile,
        headers: {
          "Content-Type": selectedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("파일 업로드 실패");
      }

      setUploadProgress(75);

      // 업로드 성공 시 강좌 컨텐츠 등록 API 호출
      const contentResponse = await apiRequest(
        API_ENDPOINTS.PRODUCT_CONTENTS.CREATE(product.id.toString()),
        {
          method: "POST",
          body: JSON.stringify({
            fileKey: presignedData.data.key,
            fileName: sanitizedFileName,
            title: title.trim(),
            description: description.trim(),
            contentType: contentType,
          }),
        }
      );

      if (!contentResponse.ok) {
        throw new Error("강좌 컨텐츠 등록 실패");
      }

      setUploadProgress(100);

      // 성공 콜백 호출
      setTimeout(() => {
        onSuccess();
      }, 500);
    } catch (error) {
      console.error("파일 업로드 오류:", error);
      setError(
        error instanceof Error ? error.message : "파일 업로드에 실패했습니다."
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">
            강좌 파일 업로드
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* 강좌 정보 */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <img
                src={product.imageUrl}
                alt={product.title}
                className="w-12 h-12 object-cover rounded"
              />
              <div>
                <h4 className="font-medium text-sm">{product.title}</h4>
                <p className="text-xs text-gray-500">{product.author}</p>
              </div>
            </div>
          </div>

          {/* 파일 선택 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              강좌 파일 선택
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                id="file-upload"
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.mp4,.mp3,.zip,.rar,.doc,.docx,.ppt,.pptx"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center space-y-2"
              >
                <Upload className="h-8 w-8 text-gray-400" />
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-blue-600 hover:text-blue-500">
                    클릭하여 파일 선택
                  </span>
                  <br />
                  또는 드래그하여 업로드
                </div>
                <div className="text-xs text-gray-400">
                  PDF, MP4, MP3, ZIP, RAR, DOC, PPT 등
                </div>
              </label>
            </div>
          </div>

          {/* 선택된 파일 정보 */}
          {selectedFile && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <File className="h-4 w-4 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-blue-700">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 제목 입력 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              컨텐츠 제목 *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="컨텐츠 제목을 입력하세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* 설명 입력 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              컨텐츠 설명
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="컨텐츠에 대한 설명을 입력하세요 (선택사항)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* 컨텐츠 타입 선택 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              컨텐츠 타입 *
            </label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {Object.values(CONTENT_TYPE_MAP).map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* 업로드 진행률 */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>업로드 중...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* 에러 메시지 */}
          {error && (
            <div className="p-3 bg-red-50 rounded-lg flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* 액션 버튼 */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isUploading}>
              취소
            </Button>
            <Button
              onClick={handleFileUpload}
              disabled={!selectedFile || !title.trim() || isUploading}
              className="flex items-center space-x-2"
            >
              {isUploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>등록 중...</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>컨텐츠 등록</span>
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
